<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import axios from 'axios';
import Modal from '../../../../components/Modal.vue';

const campaigns = ref([]);
const loading = ref(true);
const error = ref(null);

const showModal = ref(false);
const selectedCampaign = ref(null);
const campaignLogs = ref([]);
const logsLoading = ref(false);
const logsError = ref(null);

const currentPage = ref(1);
const pageSize = 10;

const paginatedLogs = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return campaignLogs.value.slice(start, start + pageSize);
});

const totalPages = computed(() =>
  Math.ceil(campaignLogs.value.length / pageSize)
);

const campaignPage = ref(1);
const campaignPageSize = 9;

const paginatedCampaigns = computed(() => {
  const start = (campaignPage.value - 1) * campaignPageSize;
  return campaigns.value.slice(start, start + campaignPageSize);
});

const totalCampaignPages = computed(() =>
  Math.ceil(campaigns.value.length / campaignPageSize)
);

const statusText = {
  pending: 'Pendiente',
  scheduled: 'Programada',
  delivered: 'Entregada',
  failed: 'Fallida',
};

const statusColor = {
  pending:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100',
  delivered:
    'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100',
  failed: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
};

const fetchCampaigns = async () => {
  try {
    const response = await axios.get('/api/v1/cron_campaigns');
    campaigns.value = response.data;
    console.log({ list_campaign: response.data });
  } catch (err) {
    error.value = 'Error al cargar campañas';
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const fetchCampaignDetails = async id => {
  logsLoading.value = true;
  logsError.value = null;
  campaignLogs.value = [];

  try {
    const response = await axios.get(`/api/v1/cron_campaigns/${id}`);
    campaignLogs.value = response.data.logs || [];
    console.log({ campaign_detail: response.data });
  } catch (err) {
    logsError.value = 'Error al cargar detalles de la campaña';
    console.error(err);
  } finally {
    logsLoading.value = false;
  }
};

const openModal = campaign => {
  selectedCampaign.value = campaign;
  showModal.value = true;
  currentPage.value = 1;
  fetchCampaignDetails(campaign.id);
};

const closeModal = () => {
  showModal.value = false;
  selectedCampaign.value = null;
  campaignLogs.value = [];
};

onMounted(() => {
  fetchCampaigns();
  const intervalId = setInterval(fetchCampaigns, 60000);
  onUnmounted(() => clearInterval(intervalId));
});
</script>

<template>
  <div class="max-w-7xl mx-auto p-6 mt-2">
    <h2 class="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">
      📦 Campañas Programadas
    </h2>

    <p v-if="loading" class="text-gray-600 dark:text-gray-300">
      Cargando campañas...
    </p>
    <p v-if="error" class="text-red-500">{{ error }}</p>

    <p v-if="campaigns.length === 0" class="text-gray-600 dark:text-gray-300">
      No se encontraron campañas
    </p>

    <div
      v-if="!loading && !error"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      <div
        v-for="campaign in paginatedCampaigns"
        :key="campaign.id"
        class="w-full min-h-[240px] flex flex-col justify-between p-6 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-md bg-white dark:bg-gray-800 hover:shadow-lg transition-shadow duration-300"
      >
        <div>
          <div class="flex items-center justify-between mb-3">
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white truncate"
            >
              {{ campaign.title || `Campaña sin nombre` }}
            </h3>
            <span
              class="text-xs font-semibold px-2 py-1 rounded"
              :class="statusColor[campaign.status]"
            >
              {{ statusText[campaign.status] || 'Desconocido' }}
            </span>
          </div>

          <div class="space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <p>
              📄 <strong>Plantilla:</strong> {{ campaign.template_name }} ({{
                campaign.template_language
              }})
            </p>
            <p>
              🕒 <strong>Envío:</strong>
              {{ new Date(campaign.send_at).toLocaleString() }}
            </p>
          </div>
        </div>

        <button
          class="w-full mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium transition-colors"
          @click="openModal(campaign)"
        >
          Ver más detalles
        </button>
      </div>
    </div>

    <div
      v-if="totalCampaignPages > 1"
      class="mt-6 flex justify-center items-center gap-2 text-sm"
    >
      <button
        class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500"
        :disabled="campaignPage === 1"
        @click="campaignPage--"
      >
        ⬅ Anterior
      </button>

      <span class="px-2">
        Página {{ campaignPage }} de {{ totalCampaignPages }}
      </span>

      <button
        class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500"
        :disabled="campaignPage === totalCampaignPages"
        @click="campaignPage++"
      >
        Siguiente ➡
      </button>
    </div>

    <Modal v-model:show="showModal" :onClose="closeModal" customWidth="60vw">
      <template #default>
        <div class="modal-content text-gray-900 dark:text-white max-h-[90vh]">
          <h3 class="text-xl font-bold mb-4">📊 Resultado de Campaña</h3>
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
          >
            <div
              class="bg-gray-100 dark:bg-gray-700 p-4 rounded-xl text-center shadow"
            >
              <p class="text-sm text-gray-600 dark:text-gray-300">Mensajes</p>
              <p class="text-xl font-bold">{{ campaignLogs.length }}</p>
            </div>
            <div
              class="bg-gray-100 dark:bg-gray-700 p-4 rounded-xl text-center shadow"
            >
              <p class="text-sm text-gray-600 dark:text-gray-300">
                Destinatarios
              </p>
              <p class="text-xl font-bold">
                {{ campaignLogs.length || '—' }}
              </p>
            </div>
            <div
              class="bg-gray-100 dark:bg-gray-700 p-4 rounded-xl text-center shadow"
            >
              <p class="text-sm text-gray-600 dark:text-gray-300">Entregados</p>
              <p class="text-xl font-bold">
                {{
                  campaignLogs.filter(
                    log => log.message_status >= 1 && log.message_status !== 3
                  ).length
                }}
              </p>
            </div>
            <div
              class="bg-gray-100 dark:bg-gray-700 p-4 rounded-xl text-center shadow"
            >
              <p class="text-sm text-gray-600 dark:text-gray-300">Leídos</p>
              <p class="text-xl font-bold">
                {{
                  campaignLogs.filter(
                    log => log.message_status >= 2 && log.message_status !== 3
                  ).length
                }}
                /
                {{ campaignLogs.length }}
              </p>
              <p class="text-xs text-gray-500 mt-1">
                {{
                  campaignLogs.length > 0
                    ? Math.round(
                        (campaignLogs.filter(
                          log =>
                            log.message_status >= 2 && log.message_status !== 3
                        ).length /
                          campaignLogs.length) *
                          100
                      ) + '% leídos'
                    : '0% leídos'
                }}
              </p>
            </div>
          </div>

          <!-- Info campaña -->
          <div class="mb-6 space-y-1 text-sm">
            <p>
              <strong>Título:</strong>
              {{ selectedCampaign?.title || 'Campaña sin nombre' }}
            </p>
            <p>
              <strong>Estado:</strong>
              {{ statusText[selectedCampaign?.status] }}
            </p>
            <p>
              <strong>Plantilla:</strong>
              {{ selectedCampaign?.template_name }} ({{
                selectedCampaign?.template_language
              }})
            </p>
            <p>
              <strong>Fecha de envío:</strong>
              {{ new Date(selectedCampaign?.send_at).toLocaleString() }}
            </p>
          </div>

          <hr class="my-4" />

          <h4 class="text-lg font-semibold mb-2">
            📞 Detalles por destinatario
          </h4>
          <p v-if="logsLoading">Cargando logs...</p>
          <p v-if="logsError" class="text-red-500">{{ logsError }}</p>

          <table
            v-if="!logsLoading && campaignLogs.length"
            class="w-full text-sm mt-2 border border-gray-200 dark:border-gray-600 rounded overflow-hidden"
          >
            <thead class="bg-gray-50 dark:bg-gray-700 text-left">
              <tr>
                <th class="py-2 px-3">📱 Número</th>
                <th class="py-2 px-3">👦 Contacto</th>
                <th class="py-2 px-3">📤 Enviado</th>
                <th class="py-2 px-3">📈 Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="log in paginatedLogs"
                :key="log.id"
                class="border-t border-gray-100 dark:border-gray-700"
              >
                <td class="py-2 px-3">{{ log.phone_number }}</td>
                <td class="py-2 px-3">{{ log.contact_name || '—' }}</td>
                <td class="py-2 px-3">
                  {{
                    log.sent_at ? new Date(log.sent_at).toLocaleString() : '—'
                  }}
                </td>
                <td class="py-2 px-3">
                  <span
                    class="inline-block px-2 py-1 text-xs rounded font-semibold"
                    :class="{
                      'bg-gray-300 text-gray-800': log.message_status === 0,
                      'bg-yellow-100 text-yellow-800': log.message_status === 1,
                      'bg-green-200 text-green-800': log.message_status === 2,
                      'bg-red-200 text-red-800': log.message_status === 3,
                      'bg-red-200 text-red-800': log.message_status === null,
                    }"
                  >
                    {{
                      log.message_status === 0
                        ? 'Enviado'
                        : log.message_status === 1
                        ? 'Entregado'
                        : log.message_status === 2
                        ? 'Leído'
                        : log.message_status === 3
                        ? 'Error'
                        : log.message_status === null
                        ? 'Mensaje Borrado'
                        : 'Desconocido'
                    }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-if="totalPages > 1"
            class="mt-4 flex justify-center items-center gap-2 text-sm"
          >
            <button
              class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500"
              :disabled="currentPage === 1"
              @click="currentPage--"
            >
              ⬅ Anterior
            </button>

            <span class="px-2">
              Página {{ currentPage }} de {{ totalPages }}
            </span>

            <button
              class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500"
              :disabled="currentPage === totalPages"
              @click="currentPage++"
            >
              Siguiente ➡
            </button>
          </div>

          <p
            v-if="!logsLoading && campaignLogs.length === 0"
            class="text-gray-500 mt-2"
          >
            No hay registros.
          </p>
        </div>
      </template>
    </Modal>
  </div>
</template>
